import { Link } from 'react-router-dom';
import { Heart, Sparkles, Crown, Users, ArrowRight, Award } from 'lucide-react';

const HERO_IMG =
  'https://customer-assets.emergentagent.com/job_574cc8bd-edef-41b9-82d3-6a702d274bcf/artifacts/81lly2fq_ChatGPT%20Image%20Jun%2015%2C%202026%2C%2011_09_34%20AM.png';
const GITA_IMG =
  'https://customer-assets.emergentagent.com/job_574cc8bd-edef-41b9-82d3-6a702d274bcf/artifacts/1fs9rvnf_ChatGPT%20Image%20Jun%2015%2C%202026%2C%2011_19_17%20AM.png';
const INTERIOR_IMG =
  'https://customer-assets.emergentagent.com/job_574cc8bd-edef-41b9-82d3-6a702d274bcf/artifacts/vxw6dwmc_ChatGPT%20Image%20Jun%2015%2C%202026%2C%2002_49_10%20PM.png';

const VALUES = [
  { icon: Heart, title: 'Authenticity', desc: "True to traditional Indian flavours from royal kitchens." },
  { icon: Sparkles, title: 'Luxury', desc: 'An elegant experience in an exotic, intimate atmosphere.' },
  { icon: Crown, title: 'Heritage', desc: 'Recipes passed down through generations of royalty.' },
  { icon: Users, title: 'Hospitality', desc: 'Warm service fit for distinguished guests.' },
];

const EXPERIENCE_POINTS = [
  'Private dining room for exclusive gatherings',
  'Elegant outdoor seating area',
  'Reservations recommended for royalty treatment',
];

const AboutPage = () => {
  return (
    <main data-testid="about-page" className="bg-[#FAF6F0] text-[#1C1917]">
      {/* ============ HERO ============ */}
      <section
        data-testid="about-hero"
        className="relative min-h-[80vh] md:min-h-[90vh] flex items-center overflow-hidden"
      >
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt="Geet fine dining wine pairing"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1C1917]/70 via-[#1C1917]/55 to-[#FAF6F0]/95" />
        </div>

        <div className="relative z-10 max-w-[1400px] w-full mx-auto px-6 md:px-12 lg:px-20 py-32">
          <div className="max-w-3xl text-center mx-auto lg:mx-0 lg:text-left">
            <div className="flex items-center justify-center lg:justify-start gap-3 mb-6">
              <span className="h-px w-12 bg-[#C9A227]" />
              <span className="font-['Cormorant_Garamond'] italic text-[#C9A227] text-sm md:text-base tracking-[0.4em] uppercase drop-shadow">
                Our Heritage
              </span>
              <span className="h-px w-12 bg-[#C9A227]" />
            </div>

            <h1 className="font-['Marcellus'] text-4xl md:text-6xl lg:text-7xl text-[#FAF6F0] mb-6 leading-tight drop-shadow-[0_6px_24px_rgba(0,0,0,0.55)]">
              Blending Vintage
              <br />
              <span className="italic font-['Cormorant_Garamond'] text-[#FFE7A8]">with the Modern.</span>
            </h1>

            <p className="font-['Cormorant_Garamond'] text-lg md:text-xl text-[#FAF6F0]/95 max-w-2xl leading-relaxed drop-shadow mx-auto lg:mx-0">
              A love letter to Indian royalty — reimagined for the modern table, told one
              dish at a time.
            </p>
          </div>
        </div>
      </section>

      {/* ============ AWARD BANNER ============ */}
      <section
        data-testid="about-award-banner"
        className="relative bg-[#1C1917] text-[#FAF6F0] py-5 md:py-6 overflow-hidden border-y border-[#C9A227]/30"
      >
        <div className="absolute inset-0 opacity-30 pointer-events-none">
          <div className="absolute top-1/2 left-1/4 -translate-y-1/2 w-72 h-72 rounded-full bg-[#C9A227] opacity-30 blur-3xl" />
          <div className="absolute top-1/2 right-1/4 -translate-y-1/2 w-72 h-72 rounded-full bg-[#7B2C2C] opacity-20 blur-3xl" />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 flex flex-col md:flex-row items-center justify-center gap-3 md:gap-5 text-center">
          <span className="font-['Cormorant_Garamond'] italic text-[#C9A227] tracking-[0.4em] uppercase text-xs md:text-sm">
            ✦ Award Winning ✦
          </span>
          <span className="hidden md:block h-5 w-px bg-[#C9A227]/40" />
          <p className="font-['Marcellus'] text-base md:text-xl tracking-wide">
            Voted <span className="text-[#FFE7A8] italic font-['Cormorant_Garamond']">Best Indian Restaurant</span> in South Africa
          </p>
        </div>
      </section>

      {/* ============ CHEF GITA STORY ============ */}
      <section
        data-testid="about-chef-story"
        className="relative py-24 md:py-32 bg-[#FAF6F0] overflow-hidden"
      >
        <div className="absolute -top-32 -right-32 w-[32rem] h-[32rem] rounded-full bg-[#5C4A1F] opacity-10 blur-3xl pointer-events-none" />
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            {/* Image */}
            <div className="lg:col-span-5 lg:sticky lg:top-32 relative group">
              <div className="relative aspect-[4/5] overflow-hidden rounded-2xl shadow-[0_25px_80px_rgba(92,74,31,0.2)]">
                <img
                  src={GITA_IMG}
                  alt="Chef Gita Jivan"
                  className="w-full h-full object-cover transition-transform duration-[1500ms] group-hover:scale-105"
                />
              </div>
              {/* Floating chef-name plate */}
              <div className="absolute -bottom-5 left-5 right-5 md:left-8 md:right-8 bg-[#1C1917] px-6 py-5 rounded-xl border border-[#C9A227]/30 shadow-xl">
                <p className="font-['Marcellus'] text-[#C9A227] text-lg md:text-xl tracking-wider">
                  Chef Gita Jivan
                </p>
                <p className="font-['Cormorant_Garamond'] text-[#FAF6F0]/70 text-sm italic mt-1">
                  Executive Chef &amp; Culinary Artist
                </p>
              </div>
            </div>

            {/* Story */}
            <div className="lg:col-span-7 pt-8 lg:pt-0 space-y-8">
              <div>
                <div className="flex items-center gap-3 mb-5">
                  <span className="h-px w-10 bg-[#5C4A1F]" />
                  <span className="font-['Cormorant_Garamond'] italic text-sm tracking-[0.3em] uppercase text-[#5C4A1F]">
                    Welcome to Geet
                  </span>
                </div>
                <h2 className="font-['Marcellus'] text-3xl md:text-4xl lg:text-5xl text-[#1C1917] mb-6 leading-tight">
                  A kitchen rooted in
                  <br />
                  <span className="italic font-['Cormorant_Garamond'] text-[#5C4A1F]">old-world charm.</span>
                </h2>
                <p className="font-['Cormorant_Garamond'] text-lg md:text-xl text-[#3F3A30] leading-relaxed">
                  Step back in time into a world of elegance and old-world charm. The Geet menu
                  explores progressive ideas in Indian cuisine while maintaining traditional
                  integrity. Chef Gita Jivan reinterprets nostalgic Indian dishes with an openness
                  toward global techniques and influences.
                </p>
              </div>

              {/* Pull quote */}
              <div className="relative bg-[#F3EBDE] border-l-4 border-[#C9A227] pl-8 pr-6 py-7 rounded-r-lg">
                <span className="absolute -top-2 left-4 font-['Marcellus'] text-6xl text-[#C9A227]/40 leading-none select-none">
                  &ldquo;
                </span>
                <p className="font-['Cormorant_Garamond'] italic text-xl md:text-2xl text-[#1C1917] leading-relaxed">
                  Embrace magic with the incredible culinary delights crafted by our chefs.
                  Chef Gita re-creates the culinary magnificence of India&apos;s royal courts.
                </p>
              </div>

              <p className="font-['Cormorant_Garamond'] text-lg md:text-xl text-[#3F3A30] leading-relaxed">
                An incandescent evening is a fine end to a great dining experience. Treat your
                taste buds to flavourful delicacies — woven into our fabric is a rich heritage
                that is second to none.
              </p>

              <p className="font-['Cormorant_Garamond'] text-lg md:text-xl text-[#3F3A30] leading-relaxed">
                Capture the essence of a cuisine that seamlessly blends the old and the new.
                Geet takes on many delectable forms — a colourful meal that entices your palate
                with an array of splendid delicacies and authentic flavours from India,
                exclusively crafted for you.
              </p>

              {/* Award card */}
              <div className="flex items-center gap-5 bg-[#1C1917] p-6 md:p-8 rounded-xl border border-[#C9A227]/30">
                <div className="w-14 h-14 md:w-16 md:h-16 border border-[#C9A227]/50 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Award className="text-[#C9A227]" size={28} />
                </div>
                <div>
                  <p className="font-['Marcellus'] text-[#FAF6F0] text-lg md:text-xl tracking-wide">
                    Best Indian Restaurant
                  </p>
                  <p className="font-['Cormorant_Garamond'] text-[#C9A227] text-base md:text-lg italic">
                    Voted #1 in South Africa
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ VALUES / WHAT DEFINES US ============ */}
      <section
        data-testid="about-values"
        className="relative py-24 md:py-32 bg-[#F3EBDE] overflow-hidden"
      >
        <div className="absolute -top-40 left-1/3 w-[32rem] h-[32rem] rounded-full bg-[#C9A227] opacity-10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-40 right-1/4 w-[28rem] h-[28rem] rounded-full bg-[#3F6249] opacity-10 blur-3xl pointer-events-none" />

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="text-center mb-16 md:mb-20">
            <div className="flex items-center justify-center gap-3 mb-5">
              <span className="h-px w-10 bg-[#C9A227]" />
              <span className="font-['Cormorant_Garamond'] italic text-sm tracking-[0.3em] uppercase text-[#C9A227]">
                Our Philosophy
              </span>
              <span className="h-px w-10 bg-[#C9A227]" />
            </div>
            <h2 className="font-['Marcellus'] text-3xl md:text-4xl lg:text-5xl text-[#1C1917] leading-tight">
              What defines <span className="italic font-['Cormorant_Garamond'] text-[#5C4A1F]">us.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {VALUES.map((item) => (
              <div
                key={item.title}
                data-testid={`value-${item.title.toLowerCase()}`}
                className="group relative bg-[#FAF6F0] border border-[#C9A227]/25 rounded-2xl p-8 text-center transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_25px_60px_rgba(92,74,31,0.15)] hover:border-[#C9A227]"
              >
                <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-[#C9A227]/10 border border-[#C9A227]/40 flex items-center justify-center transition-colors group-hover:bg-[#C9A227]/20">
                  <item.icon className="text-[#5C4A1F]" size={26} />
                </div>
                <h3 className="font-['Marcellus'] text-xl md:text-2xl text-[#1C1917] mb-3 tracking-wide">
                  {item.title}
                </h3>
                <p className="font-['Cormorant_Garamond'] text-base md:text-lg text-[#3F3A30]/85 leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ THE ROYAL EXPERIENCE ============ */}
      <section
        data-testid="about-experience"
        className="relative py-24 md:py-32 bg-[#FAF6F0] overflow-hidden"
      >
        <div className="absolute -top-32 -left-32 w-[28rem] h-[28rem] rounded-full bg-[#3F6249] opacity-10 blur-3xl pointer-events-none" />

        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            {/* Image */}
            <div className="lg:col-span-7 lg:order-2 relative group">
              <div className="relative aspect-[4/3] overflow-hidden rounded-2xl shadow-[0_25px_80px_rgba(92,74,31,0.18)]">
                <img
                  src={INTERIOR_IMG}
                  alt="Geet private dining room"
                  className="w-full h-full object-cover transition-transform duration-[1500ms] group-hover:scale-105"
                />
              </div>
              <div className="hidden md:block absolute -left-6 -top-12 font-['Marcellus'] text-[10rem] leading-none text-[#3F6249] opacity-15 select-none pointer-events-none">
                05
              </div>
            </div>

            {/* Text */}
            <div className="lg:col-span-5 lg:order-1 text-center lg:text-left">
              <div className="flex items-center gap-3 mb-5 lg:justify-start justify-center">
                <span className="h-px w-10 bg-[#3F6249]" />
                <span className="font-['Cormorant_Garamond'] italic text-sm tracking-[0.3em] uppercase text-[#3F6249]">
                  The Royal Experience
                </span>
              </div>
              <h2 className="font-['Marcellus'] text-3xl md:text-4xl lg:text-5xl text-[#1C1917] mb-6 leading-tight">
                Dining at <span className="italic font-['Cormorant_Garamond'] text-[#3F6249]">Geet.</span>
              </h2>
              <p className="font-['Cormorant_Garamond'] text-lg md:text-xl text-[#3F3A30] leading-relaxed mb-8 max-w-lg mx-auto lg:mx-0">
                Located in Brooklyn, Pretoria, Geet offers a sophisticated dining environment
                reminiscent of India&apos;s grand palaces. Whether celebrating a special occasion or
                enjoying an intimate dinner, our team ensures an unforgettable royal experience.
              </p>
              <ul className="space-y-4 mb-2 max-w-lg mx-auto lg:mx-0 text-left">
                {EXPERIENCE_POINTS.map((point) => (
                  <li key={point} className="flex items-start gap-4">
                    <span className="text-[#C9A227] text-xl leading-tight mt-0.5">✦</span>
                    <span className="font-['Cormorant_Garamond'] text-lg md:text-xl text-[#3F3A30]">
                      {point}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ============ CTA FOOTER ============ */}
      <section className="relative py-20 md:py-28 bg-[#1C1917] text-[#FAF6F0] overflow-hidden border-t border-[#C9A227]/20">
        <div className="absolute inset-0 opacity-30 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full bg-[#C9A227] opacity-30 blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 rounded-full bg-[#7B2C2C] opacity-20 blur-3xl" />
        </div>

        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <p className="font-['Cormorant_Garamond'] italic text-[#C9A227] text-sm tracking-[0.35em] uppercase mb-5">
            ✦  Come Experience It  ✦
          </p>
          <h2 className="font-['Marcellus'] text-3xl md:text-5xl mb-8 leading-tight">
            A seat at <span className="italic text-[#FFE7A8]">our table awaits.</span>
          </h2>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link
              to="/geet/contact"
              data-testid="about-reserve-btn"
              className="group inline-flex items-center gap-3 bg-[#C9A227] text-[#1C1917] px-8 py-4 font-['Cormorant_Garamond'] tracking-[0.25em] uppercase text-sm font-semibold rounded-lg hover:bg-[#FAF6F0] transition-all duration-300 shadow-lg"
            >
              Reserve a Table
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/geet/menu"
              className="inline-flex items-center gap-2 border border-[#C9A227]/60 text-[#C9A227] px-8 py-4 font-['Cormorant_Garamond'] tracking-[0.25em] uppercase text-sm rounded-lg hover:bg-[#C9A227]/10 transition-all duration-300"
            >
              View the Menu
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default AboutPage;
